import { z } from "zod";
import { AssistantCard, AssistantChangeMode, AssistantChangePolicy } from "./assistant-cards.js";
import { TerminalAccessLevel } from "./terminal.js";

/*
 * The AI support assistant (docs/assistant.md): conversations of one person,
 * each about one project or the whole organisation, with resources attached
 * as context; answers stream as Server-Sent Events. Changes run only from a
 * confirmation card the person presses (`assistant-cards.ts`).
 */

/**
 * What a context chip points at: a resource of a project, a project, a page
 * of the panel, or what the person asked about from elsewhere in the panel
 * («Спросить ассистента»): an error, a failed operation, an alert, a firewall rule.
 */
export const AssistantContextKind = z.enum([
  "project",
  "server",
  "network",
  "firewall",
  "volume",
  "load_balancer",
  "floating_ip",
  "public_ip",
  "placement_group",
  "certificate",
  "image",
  "page",
  "error",
  "operation",
  "alert",
  "firewall_rule",
  /** A screen the chat sees with every message (docs/assistant.md, "Screen context"). */
  "terminal",
  "console",
  "desktop",
]);
export type AssistantContextKind = z.infer<typeof AssistantContextKind>;

/** Who attached a chip: the page the chat was opened on, the assistant (it looked at it), the person. */
export const AssistantContextSource = z.enum(["page", "assistant", "person"]);
export type AssistantContextSource = z.infer<typeof AssistantContextSource>;

export const AssistantContextItem = z.object({
  id: z.uuid(),
  kind: AssistantContextKind,
  /** The resource's id in our API (a server id, a project id) or the page's path. */
  resourceId: z.string().min(1).max(300),
  label: z.string().max(200),
  attachedBy: AssistantContextSource,
  /** What the panel knew about it when it was attached (an error's reason and detail), for the model. */
  detail: z.string().nullable(),
});
export type AssistantContextItem = z.infer<typeof AssistantContextItem>;

/** The longest `detail` a chip carries. */
export const ASSISTANT_CHIP_DETAIL_MAX = 2000;

/** A chip to attach: the resource and the name the person sees. */
export const AssistantContextInput = z.object({
  kind: AssistantContextKind,
  resourceId: z.string().min(1).max(300),
  label: z.string().min(1).max(200),
  /** Facts for the model the label does not say (redacted by the API before it is stored). */
  detail: z.string().max(ASSISTANT_CHIP_DETAIL_MAX).optional(),
});
export type AssistantContextInput = z.infer<typeof AssistantContextInput>;

/**
 * The object the conversation is about right now (docs/assistant.md,
 * "Layers"): the focused session tab of the workspace (a terminal, console
 * or remote desktop of a server, with its Unix user), or the resource the
 * page shows. It follows the active tab unless the person pinned it.
 */
export const AssistantFocus = z.object({
  kind: z.enum(["session", "resource"]),
  /** `server`, `load_balancer`… for a resource; always `server` for a session. */
  resourceKind: z.string().regex(/^[a-z_]{1,40}$/),
  resourceId: z.string().min(1).max(300),
  label: z.string().min(1).max(200),
  /** The session's kind: a terminal, the console or a remote desktop. */
  session: z.enum(["terminal", "console", "desktop"]).optional(),
  /** The terminal's Unix user (`root`). */
  unixUser: z
    .string()
    .regex(/^[a-z_][a-z0-9_.-]{0,31}$/)
    .optional(),
  /** The person pinned it: it stays when they switch tabs. */
  pinned: z.boolean().optional(),
});
export type AssistantFocus = z.infer<typeof AssistantFocus>;

/** What became of a screen chip's snapshot with this message (layer 3, "Screen"). */
export const AssistantScreenState = z.object({
  resourceId: z.string().min(1).max(300),
  /**
   * `sent`: a snapshot goes along; `delta`: only the terminal's new lines
   * since the last one; `unchanged`: the picture is the same as the last one
   * sent; `off_screen`: the session is not on screen, nothing was taken.
   */
  state: z.enum(["sent", "delta", "unchanged", "off_screen"]),
});
export type AssistantScreenState = z.infer<typeof AssistantScreenState>;

/**
 * How long the assistant's answers are (docs/assistant.md, "Style"):
 * «Кратко» (`brief`) or «Стандартно» (`standard`). The earlier `detailed`
 * was migrated to `standard`.
 */
export const AssistantAnswerStyle = z.enum(["brief", "standard"]);
export type AssistantAnswerStyle = z.infer<typeof AssistantAnswerStyle>;

/**
 * Which model answers (docs/assistant.md, "Model routing"): `auto` picks the
 * small one for simple answers and the large one for changes, pictures and
 * diagnoses; `economy` always the small one, `quality` always the large one.
 */
export const AssistantModelMode = z.enum(["auto", "economy", "quality"]);
export type AssistantModelMode = z.infer<typeof AssistantModelMode>;

/** The longest terminal screen the panel sends with a message (the newest characters are kept). */
export const ASSISTANT_TERMINAL_SCREEN_MAX = 32_000;
/** The longest selection of the person's in a terminal that goes with a message. */
export const ASSISTANT_TERMINAL_SELECTION_MAX = 8000;

/**
 * The focused SSH terminal session when the person sends a message
 * (docs/assistant.md, "Terminal access"): the API reads its access level
 * itself; at `read` and above the screen and the selection go into the
 * model's context, at `off` they are dropped unread.
 */
export const AssistantTerminalContext = z.object({
  sessionId: z.uuid(),
  /** The screen and the scrollback lines above it, as text (redacted in the browser and again by the API). */
  screen: z.string().max(ASSISTANT_TERMINAL_SCREEN_MAX).optional(),
  /** What the person selected in that terminal, if anything. */
  selection: z.string().max(ASSISTANT_TERMINAL_SELECTION_MAX).optional(),
});
export type AssistantTerminalContext = z.infer<typeof AssistantTerminalContext>;

/** Where the person is when they send a message: the panel tells the assistant. */
export const AssistantPageContext = z.object({
  /** The page's path, e.g. `/projects/…/servers/…`. */
  route: z.string().max(300),
  /** The resource the page shows, when it shows one. */
  resource: AssistantContextInput.nullable().optional(),
  /** The reason of an error the page shows (`server_not_running`). */
  errorCode: z
    .string()
    .regex(/^[a-z0-9_]{1,80}$/)
    .nullable()
    .optional(),
  /** The browser's IANA time zone, e.g. `Europe/Berlin`. */
  timeZone: z
    .string()
    .regex(/^[A-Za-z0-9_+\-/]{1,64}$/)
    .optional(),
  language: z.enum(["ru", "en"]).optional(),
  /** The object in focus: the active session tab, or the page's resource. */
  focus: AssistantFocus.nullable().optional(),
  /** What each screen chip sent along with this message. */
  screens: z.array(AssistantScreenState).max(8).optional(),
  /** The focused SSH terminal session, its screen and the person's selection there. */
  terminal: AssistantTerminalContext.nullable().optional(),
});
export type AssistantPageContext = z.infer<typeof AssistantPageContext>;

/** What one line of tool activity is about, for the panel's wording. */
export const AssistantToolKind = z.enum([
  "get",
  "list",
  "other",
  "help",
  "proposal",
  "terminal",
  /** `explain_error`: our error reason catalogue. */
  "explain",
  /** `estimate_cost`: the cost preview of a change. */
  "estimate",
  /** `load_tools`: the assistant took a group of tools for the rest of the answer. */
  "load",
  /** `terminal_run`: a command typed into the person's own terminal session (pair mode). */
  "pair",
]);
export type AssistantToolKind = z.infer<typeof AssistantToolKind>;

/** One tool the assistant used while answering: «Посмотрел сервер web-1». */
export const AssistantToolActivity = z.object({
  id: z.string(),
  tool: z.string(),
  /** The group of its action (`servers`, `firewalls`, `help`), for the panel's wording. */
  group: z.string(),
  kind: AssistantToolKind,
  /** The resource's name when the tool looked at one. */
  target: z.string().nullable(),
  status: z.enum(["running", "ok", "error", "denied"]),
  /** The tool's arguments as the assistant gave them. */
  request: z.record(z.string(), z.unknown()),
  /** What the tool answered, redacted and shortened, as the assistant saw it. */
  result: z.string(),
  /** The answer held text addressed to an AI; the assistant treated it as data and says so. */
  injection: z.boolean(),
});
export type AssistantToolActivity = z.infer<typeof AssistantToolActivity>;

/** Why the assistant did not answer: off topic, not allowed, or the model declined. */
export const AssistantRefusal = z.enum(["out_of_scope", "disallowed", "model"]);
export type AssistantRefusal = z.infer<typeof AssistantRefusal>;

/** A log (text) or a screenshot (image) the person attached to a message. */
export const AssistantAttachmentKind = z.enum(["log", "image"]);
export type AssistantAttachmentKind = z.infer<typeof AssistantAttachmentKind>;

/** Image types the model reads; anything else is refused. */
export const ASSISTANT_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;
export const AssistantImageType = z.enum(ASSISTANT_IMAGE_TYPES);
export type AssistantImageType = z.infer<typeof AssistantImageType>;

/** Caps of one attachment and of one message (docs/assistant.md, "Attachments"). */
export const ASSISTANT_ATTACHMENT_LIMITS = {
  /** Characters of one log. */
  logChars: 200_000,
  /** Bytes of one screenshot. */
  imageBytes: 3_500_000,
  /** Longest side of a screenshot, in pixels. */
  imageSide: 8000,
  /** What the person attaches to one message. */
  perMessage: 4,
  /** Screen snapshots that go along with one message (one per screen chip). */
  screensPerMessage: 4,
  /** Characters of one terminal snapshot (the screen and its last lines of scrollback). */
  terminalChars: 32_000,
} as const;

/**
 * Where an attachment came from: a file or a paste of the person's, or a
 * snapshot of a screen the chat sees (a terminal's text, a console's or a
 * remote desktop's picture, the page's main column).
 */
export const AssistantAttachmentSource = z.enum([
  "file",
  "paste",
  "terminal",
  "console",
  "desktop",
  "page",
]);
export type AssistantAttachmentSource = z.infer<typeof AssistantAttachmentSource>;

/** Sources that are screens: their sharing is audited (`assistant.screen_shared`). */
export const ASSISTANT_SCREEN_SOURCES: ReadonlySet<AssistantAttachmentSource> = new Set([
  "terminal",
  "console",
  "desktop",
  "page",
]);

export const AssistantAttachment = z.object({
  id: z.uuid(),
  kind: AssistantAttachmentKind,
  source: AssistantAttachmentSource,
  /** The file's name, or a word for a paste. */
  name: z.string(),
  /** `text/plain` for logs. */
  mediaType: z.string(),
  /** Bytes as stored (a log after redaction). */
  size: z.number().int().nonnegative(),
  /** A log held text addressed to an AI; the assistant was told it is data. */
  injection: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type AssistantAttachment = z.infer<typeof AssistantAttachment>;

/** Base64 of an image at most `imageBytes` long. */
const IMAGE_BASE64_MAX = Math.ceil(ASSISTANT_ATTACHMENT_LIMITS.imageBytes / 3) * 4;

/** `POST /assistant/conversations/:id/attachments`: a log as text, or a screenshot as base64. */
export const CreateAssistantAttachmentRequest = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("log"),
    source: AssistantAttachmentSource.default("file"),
    name: z.string().trim().min(1).max(200),
    text: z.string().min(1).max(ASSISTANT_ATTACHMENT_LIMITS.logChars),
    /** The server a screen snapshot shows, for the audit. */
    serverId: z.uuid().optional(),
  }),
  z.object({
    kind: z.literal("image"),
    source: AssistantAttachmentSource.default("file"),
    name: z.string().trim().min(1).max(200),
    serverId: z.uuid().optional(),
    mediaType: AssistantImageType,
    data: z
      .string()
      .min(8)
      .max(IMAGE_BASE64_MAX)
      .regex(/^[A-Za-z0-9+/]+={0,2}$/),
  }),
]);
export type CreateAssistantAttachmentRequest = z.input<typeof CreateAssistantAttachmentRequest>;

export const AssistantMessage = z.object({
  id: z.uuid(),
  role: z.enum(["user", "assistant"]),
  /** Markdown for the assistant's answers, plain text for the person's. */
  text: z.string(),
  status: z.enum(["complete", "stopped", "error", "refused"]),
  refusal: AssistantRefusal.nullable(),
  tools: z.array(AssistantToolActivity),
  /** The confirmation cards this answer proposed. */
  cards: z.array(AssistantCard),
  /**
   * A `user` row that is not the person's text: the outcome of this card
   * (confirmed and run, or rejected), given to the model; the panel shows it under the card.
   */
  cardOutcome: z.uuid().nullable(),
  /**
   * A `user` row that is not the person's text: their ▶ «Выполнить» under a
   * command block of an answer, with the block's command; the panel shows it
   * as a compact event «▶ Запуск по кнопке: <command>». The API always sets it
   * (null for other rows).
   */
  runCommand: z.string().nullable().optional(),
  /** What the person attached to this message (logs, screenshots). */
  attachments: z.array(AssistantAttachment),
  /** The person's message came after the focus moved: «перешли с web-1 на web-2». */
  focusChange: z.object({ from: z.string(), to: z.string() }).nullable(),
  /** The model that wrote this answer; null for the person's messages and refusals. */
  model: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type AssistantMessage = z.infer<typeof AssistantMessage>;

export const AssistantConversationSummary = z.object({
  id: z.uuid(),
  /** Null: «Вся организация». */
  projectId: z.uuid().nullable(),
  projectName: z.string().nullable(),
  title: z.string(),
  createdAt: z.iso.datetime(),
  lastActivityAt: z.iso.datetime(),
  /** The assistant is answering in it now («думает…»). */
  running: z.boolean(),
  /** Confirmation cards waiting for the person («ждёт вас»). */
  waiting: z.number().int().nonnegative(),
  context: z.array(AssistantContextItem),
});
export type AssistantConversationSummary = z.infer<typeof AssistantConversationSummary>;

export const AssistantConversationList = z.array(AssistantConversationSummary);
export type AssistantConversationList = z.infer<typeof AssistantConversationList>;

export const AssistantConversation = AssistantConversationSummary.extend({
  messages: z.array(AssistantMessage),
});
export type AssistantConversation = z.infer<typeof AssistantConversation>;

export const CreateAssistantConversationRequest = z.object({
  projectId: z.uuid().nullable(),
  title: z.string().trim().min(1).max(120).optional(),
  context: z.array(AssistantContextInput).max(10).optional(),
});
export type CreateAssistantConversationRequest = z.input<typeof CreateAssistantConversationRequest>;

export const UpdateAssistantConversationRequest = z.object({
  title: z.string().trim().min(1).max(120),
});
export type UpdateAssistantConversationRequest = z.input<typeof UpdateAssistantConversationRequest>;

/** The longest message a person can send. */
export const ASSISTANT_MESSAGE_MAX = 8000;

export const SendAssistantMessageRequest = z.object({
  text: z.string().trim().min(1).max(ASSISTANT_MESSAGE_MAX),
  page: AssistantPageContext.optional(),
  /** Attachments uploaded to this conversation and not sent yet. */
  attachments: z
    .array(z.uuid())
    .max(ASSISTANT_ATTACHMENT_LIMITS.perMessage + ASSISTANT_ATTACHMENT_LIMITS.screensPerMessage)
    .optional(),
});
export type SendAssistantMessageRequest = z.input<typeof SendAssistantMessageRequest>;

/**
 * The events of one answer (`POST …/messages`, `text/event-stream`), each
 * `event: <type>` with this JSON as `data`.
 */
export const AssistantStreamEvent = z.discriminatedUnion("type", [
  /**
   * The person's message is stored; the answer will be `messageId`. After a
   * card was decided, `cardOutcome` names it: the "message" is its outcome, not the person's text.
   * After a ▶ «Выполнить» press, `runCommand` is the block's command: the "message" is the press.
   */
  z.object({
    type: z.literal("start"),
    userMessageId: z.uuid(),
    messageId: z.uuid(),
    cardOutcome: z.uuid().optional(),
    runCommand: z.string().optional(),
  }),
  z.object({ type: z.literal("text"), delta: z.string() }),
  /**
   * The answer's text so far is replaced: what the model wrote before it
   * called a tool («Сначала найду сервер.») is not part of the answer.
   */
  z.object({ type: z.literal("replace"), text: z.string() }),
  z.object({ type: z.literal("tool"), tool: AssistantToolActivity }),
  /** The assistant looked at a resource: it joins the conversation's chips. */
  z.object({ type: z.literal("context"), item: AssistantContextItem }),
  z.object({ type: z.literal("refusal"), refusal: AssistantRefusal }),
  /** A confirmation card was proposed, or changed (running, done, failed, rejected, expired). */
  z.object({ type: z.literal("card"), card: AssistantCard }),
  /** The answer could not be finished (`assistant_model_failed`, `assistant_timeout`). */
  z.object({ type: z.literal("error"), reason: z.string() }),
  z.object({
    type: z.literal("done"),
    messageId: z.uuid(),
    status: AssistantMessage.shape.status,
  }),
]);
export type AssistantStreamEvent = z.infer<typeof AssistantStreamEvent>;

/** Per-person limits an organisation sets. */
export const AssistantLimits = z.object({
  messagesPerMinute: z.number().int().min(1).max(60),
  messagesPerDay: z.number().int().min(1).max(5000),
  tokensPerDay: z.number().int().min(1000).max(50_000_000),
});
export type AssistantLimits = z.infer<typeof AssistantLimits>;

/** One open terminal session of the person in this browser session and its access level. */
export const AssistantTerminalSession = z.object({
  sessionId: z.uuid(),
  level: TerminalAccessLevel,
  /**
   * Until when the level stays above off and can change between the levels
   * above off without a new second factor; null at off — every rise from off
   * asks a fresh one.
   */
  expiresAt: z.iso.datetime().nullable(),
});
export type AssistantTerminalSession = z.infer<typeof AssistantTerminalSession>;

/**
 * «Доступ Автопилота к терминалу» in the status: the level a newly focused
 * session starts with (`default`: the person's own choice, else `moderated`,
 * never above `orgMax`), the person's own choice (`mine`, null: none), the
 * organisation's maximum, and the person's open sessions with their levels.
 */
export const AssistantTerminalAccess = z.object({
  default: TerminalAccessLevel,
  mine: TerminalAccessLevel.nullable(),
  orgMax: TerminalAccessLevel,
  sessions: z.array(AssistantTerminalSession),
});
export type AssistantTerminalAccess = z.infer<typeof AssistantTerminalAccess>;

/** `GET /assistant/status`: whether the chat button shows, and what the person may do with it. */
export const AssistantStatus = z.object({
  /** The installation has a model provider configured; without it the button is hidden. */
  available: z.boolean(),
  /** The organisation switched the assistant on. */
  enabled: z.boolean(),
  /** The data notice was accepted by an administrator. */
  noticeAccepted: z.boolean(),
  /** The caller administers the organisation (may switch it on, sees usage). */
  isAdmin: z.boolean(),
  /** The caller runs this installation (`ASSISTANT_OPERATOR_EMAILS`): sees its usage. */
  isOperator: z.boolean(),
  /** Where it works: every project, or these. */
  allProjects: z.boolean(),
  projectIds: z.array(z.uuid()),
  /**
   * Panel actions («Только чтение» / «Изменения»): read only, or changes
   * unlocked by the second factor in this browser session.
   */
  ...AssistantChangeMode.shape,
  /** What the organisation allows (administrators can only make it stricter). */
  changes: AssistantChangePolicy,
  /** Conversations with a confirmation card waiting for the person. */
  waiting: z.number().int().nonnegative(),
  limits: AssistantLimits,
  /** The answers' style: the organisation's default and the person's own choice (null: the default). */
  style: z.object({
    organization: AssistantAnswerStyle,
    mine: AssistantAnswerStyle.nullable(),
  }),
  terminalAccess: AssistantTerminalAccess,
  usedToday: z.object({
    messages: z.number().int().nonnegative(),
    tokens: z.number().int().nonnegative(),
  }),
});
export type AssistantStatus = z.infer<typeof AssistantStatus>;

/** `GET /assistant/settings` (administrators). */
export const AssistantSettings = AssistantLimits.extend({
  ...AssistantChangePolicy.shape,
  enabled: z.boolean(),
  noticeAcceptedAt: z.iso.datetime().nullable(),
  allProjects: z.boolean(),
  projectIds: z.array(z.uuid()),
  /** Tokens per calendar month for the whole organisation; null: no cap of our own. */
  monthlyTokenLimit: z.number().int().positive().nullable(),
  /** The organisation's default style of answers; each person may choose their own. */
  answerStyle: AssistantAnswerStyle,
  modelMode: AssistantModelMode,
  /** The installation's models: the large one, the small one, the topic filter's. */
  models: z.object({ chat: z.string(), fast: z.string(), filter: z.string() }).nullable(),
});
export type AssistantSettings = z.infer<typeof AssistantSettings>;

/** `PUT /assistant/settings`: switching it on needs the data notice accepted. */
export const UpdateAssistantSettingsRequest = AssistantLimits.extend({
  enabled: z.boolean(),
  /** The administrator accepts the data notice now. */
  acceptNotice: z.boolean().optional(),
  allProjects: z.boolean(),
  projectIds: z.array(z.uuid()).max(500),
  monthlyTokenLimit: z.number().int().positive().max(10_000_000_000).nullable(),
  /** Absent: unchanged. */
  ...AssistantChangePolicy.partial().shape,
  answerStyle: AssistantAnswerStyle.optional(),
  modelMode: AssistantModelMode.optional(),
});
export type UpdateAssistantSettingsRequest = z.input<typeof UpdateAssistantSettingsRequest>;

/**
 * `GET /assistant/preferences`: the person's own choices; null: the default
 * (the organisation's style; `moderated` for the terminal, within the organisation's maximum).
 */
export const AssistantPreferences = z.object({
  answerStyle: AssistantAnswerStyle.nullable(),
  terminalAccess: TerminalAccessLevel.nullable(),
});
export type AssistantPreferences = z.infer<typeof AssistantPreferences>;

/**
 * `PUT /assistant/preferences`: a field left out stays as it is; null goes
 * back to the default. A terminal level above the organisation's maximum is
 * refused (`terminal_access_above_max`).
 */
export const UpdateAssistantPreferencesRequest = z.object({
  answerStyle: AssistantAnswerStyle.nullable().optional(),
  terminalAccess: TerminalAccessLevel.nullable().optional(),
});
export type UpdateAssistantPreferencesRequest = z.input<typeof UpdateAssistantPreferencesRequest>;

export const AssistantUsageFigures = z.object({
  messages: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheReadTokens: z.number().int().nonnegative(),
  cacheWriteTokens: z.number().int().nonnegative(),
  /** What the provider's price list makes of it, in EUR (an estimate, nothing is charged). */
  costEur: z.string(),
});
export type AssistantUsageFigures = z.infer<typeof AssistantUsageFigures>;

/** `GET /assistant/usage?month=YYYY-MM` (administrators): tokens per person and in total. */
export const AssistantUsage = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  monthlyTokenLimit: z.number().int().positive().nullable(),
  total: AssistantUsageFigures,
  /** The same figures per model: the large, the small and the topic filter's. */
  models: z.array(AssistantUsageFigures.extend({ model: z.string() })),
  people: z.array(
    AssistantUsageFigures.extend({ userId: z.uuid(), name: z.string(), email: z.string() }),
  ),
});
export type AssistantUsage = z.infer<typeof AssistantUsage>;

/**
 * `GET /assistant/installation-usage?month=YYYY-MM` (the installation's
 * operators, `ASSISTANT_OPERATOR_EMAILS`): every organisation's tokens and
 * the installation's total against its monthly cap.
 */
export const AssistantInstallationUsage = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  /** `ASSISTANT_MONTHLY_TOKEN_LIMIT`; null: no cap. */
  monthlyTokenLimit: z.number().int().positive().nullable(),
  models: z.object({ chat: z.string(), fast: z.string(), filter: z.string() }).nullable(),
  total: AssistantUsageFigures,
  organizations: z.array(
    AssistantUsageFigures.extend({
      organizationId: z.uuid(),
      name: z.string(),
      /** People of the organisation who used the assistant this month. */
      people: z.number().int().nonnegative(),
    }),
  ),
});
export type AssistantInstallationUsage = z.infer<typeof AssistantInstallationUsage>;
